import { ApolloError, ApolloQueryResult } from "@apollo/client";
import {
  ActivityLogManyQuery,
  BroadcastManyQuery,
  EnumSurveyResponseAdminStatus,
} from "gql/generated";
import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
} from "react";
import type {
  PeriodOption,
  StatItemWithComparison,
} from "../components/Stats/useStats";
import {
  ActivityLogTimelineGroup,
} from "../components/ActivityFeed/useActivityFeed";
import { ActivityLogFilter } from "shared/utils";
import { ReadStatusFilter } from "shared/hooks/useActivityLogFeed";
import { BroadcastsTimelineGroup } from "../components/Broadcasts/useBroadcasts";

export type HomeViewsContextProviderInput = PropsWithChildren;

export type HomeViewsContextValues = {
  statsData: {
    error: ApolloError | undefined;
    widgetStats: StatItemWithComparison[];
    accountStats: StatItemWithComparison[];
    broadcastStats: StatItemWithComparison[];
    membershipStats: StatItemWithComparison[];
    membershipLedgerEnabled: boolean;
    periodOptions: PeriodOption[];
    selectedPeriodKey: string | null;
    setSelectedPeriodKey: Dispatch<SetStateAction<string | null>>;
    snapshotsLoading: boolean;
  };
  activityFeedData: {
    loading: boolean;
    isFetchingMore: boolean;
    activityLogs: ActivityLogTimelineGroup[];
    onActivityFeedClick: (
      activityLog: ActivityLogManyQuery["activityLogMany"][0],
    ) => void;
    modalOpen: { type: string; _entityId: string } | null;
    setModalOpen: Dispatch<
      SetStateAction<{ type: string; _entityId: string } | null>
    >;
    selectedEmailActivityLog: ActivityLogManyQuery["activityLogMany"][0] | null;
    setSelectedEmailActivityLog: Dispatch<
      SetStateAction<ActivityLogManyQuery["activityLogMany"][0] | null>
    >;
    fetchMore: () => Promise<void>;
    refetch: () => Promise<void>;
    hasMore: boolean;
    selectedFilters: ActivityLogFilter[];
    onFilterClick: (filter: ActivityLogFilter) => void;
    selectedTagFilters: string[];
    onTagFilterClick: (tagId: string) => void;
    readStatusFilters: ReadStatusFilter[];
    onReadStatusFilterClick: (status: ReadStatusFilter) => void;
    selectedBroadcast: BroadcastManyQuery["broadcastMany"][0] | null;
    loadingActivityItemId: string | null;
    selectedSurveyResponseId: string | null;
    setSelectedSurveyResponseId: Dispatch<SetStateAction<string | null>>;
    updateLogAdminStatus: (surveyResponseId: string, adminStatus: EnumSurveyResponseAdminStatus) => void;
    updateLogTags: (surveyResponseId: string, tags: string[]) => void;
    updateLogReviewStatus: (surveyResponseId: string, status: string) => void;
    hasUnreadInquiries: boolean;
    formsEnabled: boolean;
  };
  broadcastsData: {
    loading: boolean;
    error: ApolloError | undefined;
    broadcasts: BroadcastsTimelineGroup[];
    refetch: () => Promise<ApolloQueryResult<BroadcastManyQuery>>;
  };
};

export const HomeViewsContext = createContext<HomeViewsContextValues | null>(
  null,
);
